import ProductCard from '@app/components/product-card';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import { useGetProductsList } from '@shared/api/hooks/pcf/products';
import {
  AppProductsListEmissionsCategoryItem,
  AppProductsListSortItem,
  CollectionDetail,
  EmissionsCategoryEnum,
  ProductList,
  StatusEnum,
} from '@shared/api/types';
import { Row } from '@tanstack/react-table';
import { DataTable } from '@shared/components/data-table/data-table';
import { DataTableToolbar } from '@shared/components/data-table/data-table-toolbar';
import { useGetStaticFilterOptions } from '@shared/components/data-table/hooks/use-get-static-filter-options';
import {
  schema,
  useServerDataTable,
} from '@shared/components/data-table/hooks/use-server-data-table';
import {
  DataTableFilterField,
  DataTableProps,
} from '@shared/components/data-table/types';
import { cn } from '@shared/lib/utils';
import { useEffect } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { getProductTableColumns } from './product-table-columns';
import { ProductsTableToolbarActions } from './product-table-toolbar-actions';
import { useStatusFilterOptions } from './use-status-filter-options';

interface ProductTableProps extends Partial<DataTableProps<ProductList>> {
  type?: 'table' | 'card';
  collectionUuid?: CollectionDetail['uuid'];
  getRowCanSelect?: (row: Row<ProductList>) => boolean;
}

export interface SortOption {
  id: string;
  label: string;
}

export const productTableSchema = schema.extend({
  name: z.string().optional(),
});

export default function ProductTable({
  includeToolbarActions = true,
  enableRowSelection = false,
  enableRowClick = true,
  enableRowActions = true,
  handleRowSelection,
  preSelectedRows,
  type = 'table',
  collectionUuid,
  getRowCanSelect,
}: ProductTableProps) {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const search = productTableSchema.parse(Object.fromEntries(searchParams));

  const emissionsCategory = searchParams
    .get('emissionsCategory')
    ?.split('.') as AppProductsListEmissionsCategoryItem[];

  const status = searchParams.get('status')?.split('.') as StatusEnum[];

  const { data, isPreviousData, isFetchedAfterMount, isLoading, isError } =
    useGetProductsList({
      page: search.page,
      page_size: search.page_size,
      emissions_category: emissionsCategory,
      search: search.name || '',
      status: status,
      sort: [search.sort] as AppProductsListSortItem[],
      collection: collectionUuid || undefined,
    });

  if (isError) throw new Error();

  const { results: products, count } = data || {};

  const pageCount = count ? Math.ceil(count / (search.page_size || 10)) : 0;

  const filterFields: DataTableFilterField<ProductList>[] = [
    {
      label: 'Products',
      value: 'name',
      placeholder: 'Search products...',
    },
    {
      label: 'Rating',
      value: 'emissionsCategory',
      options: useGetStaticFilterOptions(EmissionsCategoryEnum),
    },
    {
      label: 'Status',
      value: 'status',
      options: useStatusFilterOptions(),
    },
  ];

  const { table } = useServerDataTable({
    data: products || [],
    columns: getProductTableColumns(),
    pageCount,
    filterFields,
    defaultPerPage: 10,
    columnVisibilityState: {
      uuid: false,
      select: enableRowSelection || false,
      actions: enableRowActions || false,
    },
    rowId: 'uuid',
    preSelectedRows: preSelectedRows,
    enableRowSelection: getRowCanSelect ?? true,
  });
  const tableState = table.getState();

  useEffect(() => {
    if (handleRowSelection) {
      handleRowSelection(tableState.rowSelection);
    }
  }, [handleRowSelection, tableState.rowSelection]);

  const sortOptions: SortOption[] = table
    .getAllColumns()
    .filter((column) => column.getCanSort() && column.columnDef.meta?.sorting)
    .flatMap((column) => [
      {
        id: column.id,
        label: column.columnDef.meta!.sorting!.ascLabel,
      },
      {
        id: `-${column.id}`,
        label: column.columnDef.meta!.sorting!.descLabel,
      },
    ]);

  return (
    <DataTable
      table={table}
      className="bg-gray-00"
      headerConfig={{ className: 'bg-primary' }}
      showHeader={type === 'card' ? false : true}
      rowClassName={() =>
        cn('', {
          'bg-gray-25 border-none': type === 'card',
          'hover:bg-gray-50': enableRowClick && type === 'table',
        })
      }
      onRowClick={(row) => {
        if (!enableRowClick) return;
        const uuid = row?.original.uuid;
        const status = row?.getValue('status');

        status === StatusEnum.draft ||
        (status === StatusEnum.in_progress &&
          row.original.assessmentDate === null)
          ? navigate(
              generatePath(pcfRoutes.PRODUCT_EDIT, {
                product_id: uuid,
              })
            )
          : generatePath(pcfRoutes.PRODUCT_DETAIL, {
              product_id: uuid,
            });
      }}
      customRowItem={
        type === 'card'
          ? (row) => (
              <ProductCard
                product={row.original}
                className="mb-3 cursor-pointer"
                onClick={() => {
                  if (!enableRowClick) return;
                  const uuid = row?.original.uuid;
                  const status = row?.getValue('status');
                  if (
                    (row.original.assessmentDate === null &&
                      status === StatusEnum.in_progress) ||
                    status === StatusEnum.draft
                  ) {
                    navigate(
                      generatePath(pcfRoutes.PRODUCT_EDIT, {
                        product_id: uuid,
                      })
                    );
                  } else {
                    navigate(
                      generatePath(pcfRoutes.PRODUCT_DETAIL, {
                        product_id: uuid,
                      })
                    );
                  }
                }}
              />
            )
          : undefined
      }
      isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
      enablePagination
    >
      <DataTableToolbar
        table={table}
        filterFields={filterFields}
        sortOptions={type === 'card' ? sortOptions : undefined}
      >
        {includeToolbarActions && (
          <ProductsTableToolbarActions collectionUuid={collectionUuid} />
        )}
      </DataTableToolbar>
    </DataTable>
  );
}
